import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { createClient } from '@/lib/supabase-client'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import PublicHeader from '@/components/PublicHeader'
import PublicFooter from '@/components/PublicFooter'

interface BlogPost {
  id: string
  slug: string
  title: string
  excerpt: string | null
  content: string
  cover_image_url: string | null
  tags: string[] | null
  published_at: string | null
  created_at: string | null
}

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>()
  const supabase = createClient()
  const [post, setPost] = useState<BlogPost | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    const loadPost = async () => {
      if (!slug) return
      setLoading(true)
      setError(null)

      try {
        const { data, error } = await supabase
          .from('blog_posts')
          .select('id, slug, title, excerpt, content, cover_image_url, tags, published_at, created_at')
          .eq('slug', slug)
          .eq('is_published', true)
          .maybeSingle()

        if (error) {
          throw error
        }

        if (!data) {
          setError('Post not found')
        }

        setPost(data as BlogPost | null)
      } catch (err: any) {
        console.error('Error loading blog post', err)
        setError(err.message || 'Failed to load blog post')
      } finally {
        setLoading(false)
      }
    }

    loadPost()
  }, [slug, supabase])

  const handleCopyLink = async () => {
    try {
      const url = window.location.href
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // ignore clipboard errors
    }
  }

  const handleNativeShare = async () => {
    if (!post) return
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt || undefined,
          url: window.location.href,
        })
      } catch {
        // user cancelled or share failed
      }
    }
  }

  const currentUrl = typeof window !== 'undefined' ? window.location.href : ''

  let date: Date | null = null
  if (post?.published_at || post?.created_at) {
    date = new Date(post.published_at || post.created_at || '')
  }

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 text-gray-900 dark:text-white transition-colors">
      <PublicHeader />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-6">
          <Link
            to="/blog"
            className="inline-flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
          >
            <svg
              className="w-4 h-4 mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to blog
          </Link>
        </div>

        {loading && (
          <div className="flex justify-center py-16">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 dark:border-primary-400" />
              <p className="mt-4 text-gray-600 dark:text-gray-400 text-sm">
                Loading post...
              </p>
            </div>
          </div>
        )}

        {!loading && error && (
          <div className="max-w-xl mx-auto">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-sm text-red-700 dark:text-red-300">
                {error}
              </p>
            </div>
          </div>
        )}

        {!loading && !error && post && (
          <article className="glass-card rounded-xl overflow-hidden">
            {post.cover_image_url && (
              <img
                src={post.cover_image_url}
                alt={post.title}
                className="w-full h-72 object-cover"
              />
            )}

            <div className="p-6 sm:p-8">
              <header className="mb-6">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  {date && (
                    <span className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      {date.toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>
                  )}
                  {post.tags && post.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {post.tags.map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <h1 className="text-3xl sm:text-4xl font-bold mb-3">
                  {post.title}
                </h1>
                {post.excerpt && (
                  <p className="text-gray-600 dark:text-gray-400 text-base">
                    {post.excerpt}
                  </p>
                )}
              </header>

              <div className="prose prose-slate dark:prose-invert max-w-none">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    a: ({ href, children, ...props }) => (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 underline font-medium"
                        {...props}
                      >
                        {children}
                      </a>
                    ),
                  }}
                >
                  {post.content}
                </ReactMarkdown>
              </div>

              <footer className="mt-8 pt-6 border-t border-gray-200 dark:border-slate-800">
                <div className="flex flex-wrap items-center gap-3">
                  {'share' in navigator && (
                    <button
                      type="button"
                      onClick={handleNativeShare}
                      className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary-600 text-white hover:bg-primary-700 transition"
                    >
                      <svg
                        className="w-4 h-4 mr-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 12v.01M12 20l8-8-8-8"
                        />
                      </svg>
                      Share
                    </button>
                  )}

                  <a
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
                      post.title
                    )}&url=${encodeURIComponent(currentUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold bg-sky-500 text-white hover:bg-sky-600 transition"
                  >
                    X / Twitter
                  </a>

                  <a
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                      currentUrl
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold bg-sky-700 text-white hover:bg-sky-800 transition"
                  >
                    LinkedIn
                  </a>

                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                      currentUrl
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 transition"
                  >
                    Facebook
                  </a>

                  <button
                    type="button"
                    onClick={handleCopyLink}
                    className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 text-gray-800 dark:bg-slate-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-slate-700 transition"
                  >
                    <svg
                      className="w-4 h-4 mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2M8 16h8a2 2 0 002-2v-4M8 16l-2 2m2-2l2 2"
                      />
                    </svg>
                    {copied ? 'Link copied' : 'Copy link'}
                  </button>
                </div>
              </footer>
            </div>
          </article>
        )}
      </main>

      <PublicFooter />
    </div>
  )
}

